import { datadogLogs } from '@datadog/browser-logs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { identify, trackEvent } from '@fff-web/fff-analytics';
import {
  Constants,
  HCaptchaContainer,
  SubscriptionUpdatePaymentModal,
} from '@fff-web/fff-payments';
import AllErrorMessage from '../../components/AllErrorMessage';
import Layout from '../../components/Layout';
import useShowErrorOrRedirectThankYouPage from '../../hooks/useShowPayErrorOrThankYouPage';
import { executeCaptcha } from '../../utils/functions/executeCaptcha';
import { redirectToBaseSite, redirectToLogin } from '../../utils/redirectMethods';
import { ProRatedUpgrade } from './ProRatedUpgrade';
import {
  PRO_RATED_UPGRADE_MESSAGES,
  trackSubscriptionUpgrade,
  useTrackProRatedUpgrade,
} from './ProRatedUpgradeUtils';
import { useUpgradeConfirmationState } from './useUpgradeConfirmationState';

function ProRatedUpgradeContainer() {
  const { loading, setLoading, captchaRef, prices, userInfo, navigate, couponCode } =
    useUpgradeConfirmationState();
  const { chatbotAppliedLab: showChatbot } = useFlags();

  const { showRetry, showPayment, errorPaymentMessage, changeSubscription, onClose, isLoading } =
    useShowErrorOrRedirectThankYouPage(
      navigate,
      PRO_RATED_UPGRADE_MESSAGES.thanksPage,
      Constants.SEASONAL_PLAN_CODE,
      [{ type: 'coupon_code', code: couponCode }],
    );

  useTrackProRatedUpgrade();

  const onSubmitSubscription = async () => {
    try {
      const captchaToken = await executeCaptcha(captchaRef);
      setLoading(true);

      await changeSubscription(Constants.ANNUAL_PLAN_CODE, captchaToken);

      identify({
        userId: userInfo.user.userId.toString(),
        traits: {
          planCode: userInfo.user.planCode,
          memberStatus: userInfo.user.subscriptionState.toLowerCase(),
          role: userInfo.user.subscriptionState.toLowerCase(),
        },
      });

      trackSubscriptionUpgrade({
        ctaName: PRO_RATED_UPGRADE_MESSAGES.ctaName,
        trackName: PRO_RATED_UPGRADE_MESSAGES.trackName,
        newPlan: Constants.ANNUAL_PLAN_CODE as keyof typeof Constants,
        previousPlan: userInfo.user.subscriptionType as keyof typeof Constants,
      });
    } catch (error) {
      datadogLogs.logger.error(PRO_RATED_UPGRADE_MESSAGES.subscriptionError, {}, error);

      trackEvent({
        ctaName: PRO_RATED_UPGRADE_MESSAGES.trackName,
        properties: {
          plan_selected: Constants.ANNUAL_PLAN_CODE,
          status: 'error',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const { loggedIn } = userInfo;
  const userPlanCode = userInfo?.user.planCode;

  if (!loggedIn) {
    redirectToLogin();
    return null;
  }

  if (loggedIn && userPlanCode === Constants.ANNUAL_PLAN_CODE) {
    redirectToBaseSite();
    return null;
  }

  return (
    <Layout
      isLoading={loading || isLoading}
      showError={showRetry}
      redirectIfNoAuth
      ErrorComponent={() => <AllErrorMessage subjectAction="upgrade" onButtonClick={onClose} />}
      hideChatWidget={!showChatbot}
    >
      <ProRatedUpgrade
        submitUpgrade={onSubmitSubscription}
        loading={loading}
        annualPrice={prices.annualPrice?.annualPrice}
        seasonalPrice={prices.seasonalPrice?.price}
      />
      <HCaptchaContainer ref={captchaRef} />
      <SubscriptionUpdatePaymentModal
        onClose={onClose}
        subtitle={errorPaymentMessage}
        isOpen={showPayment}
        onSuccess={onSubmitSubscription}
        hasError={!!errorPaymentMessage}
      />
    </Layout>
  );
}

export default ProRatedUpgradeContainer;
